import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";

interface SiteFaqRecord {
  id?: string;
  group_title: string;
  question: string;
  answer: string;
  sort_order: number;
  published: boolean;
}

const EMPTY_FAQ: SiteFaqRecord = {
  group_title: "General",
  question: "",
  answer: "",
  sort_order: 0,
  published: true,
};

export function FaqManager() {
  const [faqs, setFaqs] = useState<SiteFaqRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadFaqs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("site_faqs")
        .select("*")
        .order("group_title", { ascending: true })
        .order("sort_order", { ascending: true });

      if (error) throw error;

      setFaqs(
        ((data as SiteFaqRecord[]) || []).length > 0
          ? (data as SiteFaqRecord[])
          : [{ ...EMPTY_FAQ }]
      );
    } catch (error) {
      console.error("Failed to load FAQs", error);
      toast.error("Failed to load FAQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFaqs();
  }, []);

  const updateFaq = (index: number, field: keyof SiteFaqRecord, value: string | number | boolean) => {
    setFaqs((prev) =>
      prev.map((faq, faqIndex) => (faqIndex === index ? { ...faq, [field]: value } : faq))
    );
  };

  const handleSave = async () => {
    const cleanedFaqs = faqs
      .map((faq, index) => ({
        ...faq,
        group_title: faq.group_title.trim() || "General",
        question: faq.question.trim(),
        answer: faq.answer.trim(),
        sort_order: index,
      }))
      .filter((faq) => faq.question && faq.answer);

    if (cleanedFaqs.length === 0) {
      toast.error("Add at least one FAQ");
      return;
    }

    setSaving(true);
    try {
      const { error: deleteError } = await supabase.from("site_faqs").delete().gte("sort_order", 0);
      if (deleteError) throw deleteError;

      const { error: insertError } = await supabase.from("site_faqs").insert(
        cleanedFaqs.map((faq) => ({
          group_title: faq.group_title,
          question: faq.question,
          answer: faq.answer,
          sort_order: faq.sort_order,
          published: faq.published,
        }))
      );

      if (insertError) throw insertError;
      toast.success("FAQs updated");
      await loadFaqs();
    } catch (error) {
      console.error("Failed to save FAQs", error);
      toast.error("Failed to save FAQs");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>Website FAQs</CardTitle>
            <CardDescription>Manage grouped FAQs for the public FAQ page.</CardDescription>
          </div>
          <Button
            variant="outline"
            onClick={() => setFaqs((prev) => [...prev, { ...EMPTY_FAQ, sort_order: prev.length }])}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add FAQ
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading FAQs...
          </div>
        ) : (
          <>
            {faqs.map((faq, index) => (
              <div key={`${faq.id || "new"}-${index}`} className="rounded-xl border p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-medium">FAQ {index + 1}</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setFaqs((prev) => prev.filter((_, faqIndex) => faqIndex !== index))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Group Title</Label>
                    <Input
                      value={faq.group_title}
                      onChange={(event) => updateFaq(index, "group_title", event.target.value)}
                      placeholder="Merchant Benefits"
                    />
                  </div>

                  <div className="flex items-center justify-between rounded-xl border p-4">
                    <div>
                      <p className="font-medium">Published</p>
                      <p className="text-sm text-muted-foreground">Show this FAQ publicly.</p>
                    </div>
                    <Switch
                      checked={faq.published}
                      onCheckedChange={(checked) => updateFaq(index, "published", checked)}
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label>Question</Label>
                    <Input
                      value={faq.question}
                      onChange={(event) => updateFaq(index, "question", event.target.value)}
                      placeholder="How does Splitkart grow my AOV?"
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label>Answer</Label>
                    <Textarea
                      rows={4}
                      value={faq.answer}
                      onChange={(event) => updateFaq(index, "answer", event.target.value)}
                      placeholder="Write the answer shown on the FAQ page."
                    />
                  </div>
                </div>
              </div>
            ))}

            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save FAQs
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
